import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

/** 画面の題（各画面の index の h6）の id。表の外枠の読み上げの名前に使う（EV-310）。 */
export const PAGE_TITLE_ID = 'page-title';
/** タブの button の id（reusable/tab）。表の外枠の読み上げの名前に使う（EV-310）。 */
export const tabId = (value: string) => `tab-${value}`;

interface TableScrollProps {
  children: React.ReactNode;
  /**
   * 外枠の読み上げの名前を取る要素の id（空白で区切る。例: `${PAGE_TITLE_ID} ${tabId('allsubmission')}` で
   * 「アルコール検査 全ての報告」）。新しい文言は足さず、画面の題とタブの文言を使う（EV-310）。
   */
  labelledBy?: string;
}

/**
 * 表の横送り（EV-301。PM の判断 2026-10-12: 表を横に送れるようにする）。
 *
 * 狭い幅（768px など）では、表の列が中身（EV-292・EV-297 の直しで、語の途中で折らない証跡の欄など）より狭くなり、
 * 列どうしが重なっていた。表の見出しと行をこの外枠で包み、中身に最小の幅を持たせる。外枠より中身が広いときは、
 * 外枠の中だけが横に送れる（ページ全体は横に送れない）。
 *
 * 最小の幅（47rem＝752px）は、1024px のときの表の幅（755px）以下で、列が崩れない幅を実ブラウザで測って決めた
 * （docs/testing/parallel-wave22_2026-10-12/table-scroll/README.md）。
 *
 * EV-304（docs/testing/parallel-wave23_2026-10-13/admin-layout/README.md）。外枠を送るかどうかは、画面の幅（lg）ではなく、
 * 実際の外枠の幅で決める（外の div を container-type: inline-size にし、container query で外枠の幅が境以上のときだけ
 * overflow-x: visible。境は EV-316 で 46.75rem にした。下の段落）。スクロールバーが場所を取る環境（Windows の既定など）では、
 * 画面の幅が 1024px 以上でも外枠が 752px より狭くなり、lg で切っていたときは表が外枠を出ていた。境以上の幅では外枠を送らない。
 * そのとき見出しの長い英語の Device Time Zone は、外枠を越えるときだけ折り返して外枠の中に収める（EV-321。
 * ExceedAlcoholLimitsTable。#248 の時点では1行のまま表の右端から少し出していた）。
 *
 * 外枠は relative にする（PR #244 の照合の P1）。表の中の position: absolute の要素（DeviceTimeZone の読み上げ用の
 * sr-only など）は、外枠が static だと body を基準にし、外枠の overflow で切られずに文書の幅を広げる。
 *
 * EV-316（docs/testing/parallel-wave25_2026-10-14/table-width/README.md）。外枠が中身の最小の幅（47rem）に 4px（0.25rem）以内で
 * 足りないだけのとき（スクロールバーが場所を取る環境の 1038〜1040px など）は、1〜3px のための横送りとスクロールバーを出さず、
 * 中身を外枠の幅に合わせる（container query の境を 46.75rem にし、その幅では中身の最小の幅を外す。748〜751px は崩れない幅
 * 700px より十分広い）。境より狭い外枠（17px のスクロールバーの 1024・1032px、768px など）は今までどおり 47rem の中身を外枠の中で送る。
 * 酒気帯び以上の報告の最後の列の見出し（EV-321）は、同じ境で折り返せるようにしている（ExceedAlcoholLimitsTable）。
 *
 * EV-310。外枠は role="region" で、読み上げの名前（labelledBy）を持つ。横に送れるとき（中身が外枠より広いとき）だけ
 * Tab の順に入り（tabIndex=0）、フォーカスを置いて左右の矢印キーで送れる。送れないときは Tab の順に入れない。
 * EV-316。1px でも送れるなら Tab の順に入れる（以前は 1px の許容があり、1px だけ送れる外枠が Tab で止まらなかった）。
 * 外枠の中の操作できる要素へ Tab で移ったときも、ブラウザが外枠を送ってその要素を見せる。
 */
const TableScroll: React.FC<TableScrollProps> = ({ children, labelledBy }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setScrollable(el.scrollWidth > el.clientWidth && getComputedStyle(el).overflowX !== 'visible');
  }, []);
  useLayoutEffect(() => {
    update();
    const el = ref.current;
    window.addEventListener('resize', update);
    let observer: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(el);
      if (el.firstElementChild) observer.observe(el.firstElementChild);
    }
    return () => {
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [update]);

  return (
    <div className="[container-type:inline-size]">
      <div
        ref={ref}
        role="region"
        aria-labelledby={labelledBy}
        tabIndex={scrollable ? 0 : undefined}
        className="relative overflow-x-auto [@container(min-width:46.75rem)]:overflow-x-visible"
      >
        <div className="min-w-[47rem] [@container(min-width:46.75rem)]:min-w-0">{children}</div>
      </div>
    </div>
  );
};

export default TableScroll;
